import { useRef, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { useHorizontalResize } from 'hooks/useHorizontalResize'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import * as Styled from '../styled'
import { IAudioItem } from 'types/audio'
import { useAudioItemResize } from '../useAudioItemResize'
import Text from 'common-components/Text'
import AudioWave from '../AudioWave'
import { ttsSelectors } from 'store/features/tts/selectors'
import { getAudioItemBackgroundColor } from '../helpers'
import { useGetTtsItemGeneratedState } from 'containers/SubSidebar/TtsContent/hooks'
import { TRACK_ITEM_CLASS_NAME } from 'consts'
import { useTimelineContext } from 'context/TimelineContext'
import { secondsToPixels } from 'utils/timeline'
import { editorSelectors } from 'store/features/editor/selectors'
import { isBlobUrl } from 'utils'

import { COLORS } from 'consts/colors'
import { PROJECT_AUDIO_TYPES } from 'consts/audio'
import { getIsLibrarySound } from 'store/features/projectData/utils'

import { TRootState } from 'store/types'
import { dispatch } from 'store'
import { projectDataThunks } from 'store/features/projectData/thunks'

const MAX_ALLOWED_FILE_SIZE_BYTE = 15 * 1024 * 1024

interface ITrackItemProps {
  data: IAudioItem
  isActive: boolean
  isOverlappingPlayHead: boolean
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void
  updateAudioItems: (audioItems: IAudioItem[]) => void
  isResizeBtnsVisible?: boolean
  selectedBorderColor: string
  selectedBorderSize: number
}

const TrackItem = ({
  data,
  isActive,
  onClick,
  updateAudioItems,
  isResizeBtnsVisible,
  selectedBorderColor,
  selectedBorderSize
}: ITrackItemProps) => {
  const ref = useRef<HTMLDivElement>(null)

  const metaData = useSelector((state: TRootState) =>
    projectDataSelectors.soundMetaDataByAssetId(state, data.assetId)
  )
  const isSoundsMetaDataEmpty = useSelector(
    projectDataSelectors.isSoundsMetaDataEmpty
  )
  const fileSize = metaData?.fileSize
  const isDraftAudio = !useGetTtsItemGeneratedState(Number(data.assetId))
  const { scrollableContainerRef } = useTimelineContext()
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const backgroundColor = useMemo(() => {
    return getAudioItemBackgroundColor(data.type, isDraftAudio)
  }, [data.type, isDraftAudio])

  const audioItems = useSelector(projectDataSelectors.multilineAudios)
  const isAudioItemActionsDisabled = useSelector(
    ttsSelectors.isGenerationInProcess
  )

  // temporal solution to diff lib/user sounds, all the lib sounds have "_second " part,
  // we do not have any type or data diff between user and lib sounds,
  // and in bk part, we do not pass the lib sound filesize to fe,(restricted info)
  const isNotUserSound =
    data.type !== PROJECT_AUDIO_TYPES.SOUND || getIsLibrarySound(data.mediaPath)

  const isAllowedToDecodeWaves =
    isNotUserSound ||
    (fileSize !== undefined && fileSize < MAX_ALLOWED_FILE_SIZE_BYTE)

  const isTitleVisible = !isActive || !isAllowedToDecodeWaves
  const { onResizeStart, onResizeEnd, handleLeftResize, handleRightResize } =
    useAudioItemResize({
      itemId: data.id,
      lineIndex: data.lineIndex,
      audioItems,
      updateAudioItems
    })

  const { onLeftResize, onRightResize } = useHorizontalResize({
    ref,
    onResizeEnd,
    onResizeStart,
    handleLeftResize,
    handleRightResize,
    scrollableContainerRef,
    left: secondsToPixels(data.projectStart, timelineRulerSliceDuration)
  })

  const isBlobPath = useMemo(
    () => data?.mediaPath && isBlobUrl(data.mediaPath),
    [data?.mediaPath]
  )

  const finalTitle = useMemo(() => {
    const regExp =
      /(\.(mp3|wav|ogg|oga|opus|flac|m4a|weba|aiff|alac|caf|amr)$)/i
    if (regExp.test(data.title)) {
      return data.title
    }
    return `${data.title}.mp3`
  }, [data.title])

  useEffect(() => {
    if (isNotUserSound || fileSize || isSoundsMetaDataEmpty || !data.assetId)
      return
    dispatch(projectDataThunks.getSoundMetaDataById(data.assetId))
  }, [data.assetId, isNotUserSound, fileSize, isSoundsMetaDataEmpty])

  return (
    <Styled.TrackItem
      $isActive={isActive}
      $isDisabled={isAudioItemActionsDisabled}
      onClick={onClick}
      ref={ref}
      id={data.id}
      $backgroundColor={backgroundColor}
      className={TRACK_ITEM_CLASS_NAME}
      data-type={data.type}
      data-asset-id={data.assetId}
    >
      <Styled.TrackItemWrapper
        isActive={isActive}
        onLeftResize={onLeftResize}
        onRightResize={onRightResize}
        isResizeBtnsVisible={isResizeBtnsVisible}
        selectedBorderColor={selectedBorderColor}
        selectedBorderSize={selectedBorderSize}
      >
        {isTitleVisible ? (
          <Styled.AudioNameWrapper>
            <Text type="h5" color="white" ellipsis>
              {finalTitle}
            </Text>
            {isBlobPath && (
              <Styled.BlobLoader
                size={16}
                wrapperSize={20}
                loaderColor={COLORS.white}
              />
            )}
          </Styled.AudioNameWrapper>
        ) : null}
        {isAllowedToDecodeWaves && (
          <AudioWave
            audioItem={data}
            isActive={isActive}
            isDraft={isDraftAudio}
          />
        )}
      </Styled.TrackItemWrapper>
    </Styled.TrackItem>
  )
}

export default TrackItem
